/**
 * Centralized analytics helper — pushes to GTM dataLayer.
 *
 * Performance:
 * - Zero deps, no listeners, no setState.
 * - Try/catch silent: never breaks the app if dataLayer is missing.
 * - GTM is loaded lazily in src/main.tsx; events pushed before GTM
 *   are queued and replayed automatically.
 *
 * See src/lib/analytics/trackingPlan.md for the full plan.
 */

declare global {
  interface Window {
    dataLayer: any[];
  }
}

type Params = Record<string, unknown>;

let globalCtx: Params = {};

/** Set context merged into every push (country/lang/currency, etc.). */
export function setGlobalContext(ctx: Params) {
  globalCtx = { ...globalCtx, ...ctx };
}

export function getGlobalContext(): Params {
  return globalCtx;
}

/** Push an event to the dataLayer. Silent on any error. */
export function track(event: string, params: Params = {}) {
  try {
    if (typeof window === "undefined") return;
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(cleanUndefined({ event, ...globalCtx, ...params }));
  } catch {
    /* noop */
  }
}

/** Schedule a push when the browser is idle (post-LCP friendly). */
export function trackIdle(event: string, params: Params = {}) {
  if (typeof window === "undefined") return;
  const run = () => track(event, params);
  if ("requestIdleCallback" in window) {
    (window as any).requestIdleCallback(run, { timeout: 2000 });
  } else {
    setTimeout(run, 0);
  }
}

function cleanUndefined<T extends Params>(obj: T): T {
  const out: Params = {};
  for (const k in obj) {
    const v = obj[k];
    if (v !== undefined) out[k] = v;
  }
  return out as T;
}
